import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import useIsMobile from "../../hooks/useIsMobile";
import Logo from "../common/Logo";
import { NAV_LINKS } from "../../data/constants";
import { colors, fonts, btnOutline, btnGhost, maxW } from "../../styles/theme";

export default function Navbar() {
  const mob = useIsMobile();
  const { pathname } = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", fn);
    return () => window.removeEventListener("scroll", fn);
  }, []);
  useEffect(() => setOpen(false), [pathname]);

  return (
    <nav
      style={{
        position: "fixed", top: 0, left: 0, right: 0, zIndex: 1000,
        background: colors.paper,
        borderBottom: `1px solid ${colors.border}`,
        boxShadow: scrolled ? "0 4px 18px rgba(27,23,18,0.06)" : "none",
        transition: "box-shadow .3s",
      }}
    >
      <div style={{ ...maxW, display: "flex", alignItems: "center", justifyContent: "space-between", height: 74, padding: "0 6%" }}>
        <Link to="/" style={{ ...btnGhost, display: "flex", alignItems: "center", gap: 11, textDecoration: "none" }}>
          <Logo size={30} />
          <span style={{ fontFamily: fonts.display, fontWeight: 600, fontSize: 20, color: colors.ink }}>
            Charith
          </span>
        </Link>

        {!mob && (
          <div style={{ display: "flex", alignItems: "center", gap: 30 }}>
            {NAV_LINKS.map((l) => {
              const active = pathname === l.path;
              return (
                <NavLink
                  key={l.path}
                  to={l.path}
                  style={{
                    position: "relative",
                    textDecoration: "none",
                    fontFamily: fonts.body,
                    fontWeight: 500,
                    fontSize: 14,
                    padding: "6px 0",
                    color: active ? colors.rust : colors.inkDim,
                    transition: "color .2s",
                  }}
                >
                  {l.label}
                  {active && (
                    <motion.div
                      layoutId="navUnderline"
                      style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 1.5, background: colors.rust }}
                      transition={{ type: "spring", stiffness: 380, damping: 32 }}
                    />
                  )}
                </NavLink>
              );
            })}
            <motion.div whileHover={{ backgroundColor: colors.ink, color: colors.cream }} transition={{ duration: 0.2 }}>
              <Link to="/contact" style={{ ...btnOutline, fontSize: 13, padding: "10px 20px", textDecoration: "none", display: "inline-block" }}>
                Start a Project
              </Link>
            </motion.div>
          </div>
        )}

        {mob && (
          <button onClick={() => setOpen((o) => !o)} style={{ background: "none", border: "none", color: colors.ink, cursor: "pointer", padding: 4, lineHeight: 1, display: "flex" }}>
            {open ? <X size={24} strokeWidth={1.6} /> : <Menu size={24} strokeWidth={1.6} />}
          </button>
        )}
      </div>

      <AnimatePresence>
        {mob && open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.28, ease: "easeInOut" }}
            style={{ overflow: "hidden", background: colors.paper, borderTop: `1px solid ${colors.border}` }}
          >
            <div style={{ padding: "10px 6% 24px" }}>
              {NAV_LINKS.map((l) => {
                const active = pathname === l.path;
                return (
                  <Link
                    key={l.path}
                    to={l.path}
                    style={{
                      display: "block", width: "100%", textDecoration: "none",
                      fontFamily: fonts.display, fontWeight: 500, fontSize: 20,
                      padding: "14px 0", color: active ? colors.rust : colors.ink,
                      borderBottom: `1px solid ${colors.border}`,
                    }}
                  >
                    {l.label}
                  </Link>
                );
              })}
              <Link to="/contact" style={{ ...btnOutline, width: "100%", marginTop: 20, fontSize: 15, padding: 14, textDecoration: "none", display: "block", textAlign: "center", boxSizing: "border-box" }}>
                Start a Project
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
}
